import React from 'react';
import {
  PROCUREMENT_PDF_HEIGHT,
  PROCUREMENT_PDF_ROW_HEIGHT,
  PROCUREMENT_PDF_WIDTH,
  PROCUREMENT_SHIPPER_NAME,
  PROCUREMENT_SUPPLIER,
  buildInvoicePages,
  formatArrivalAtKo,
  formatInvoiceCellNumber,
  formatShipmentDateKo,
  formatShipmentQtyDisplay,
  getInvoiceMemoFlags,
  resolveDestinationPhones,
  type ProcurementInvoicePdfData,
  type ProcurementInvoicePdfPage,
} from '../utils/procurementInvoicePdf';

const theme = {
  /** 팩스 전송 시 연한 회색 선이 사라지므로 진한 검정에 가깝게 */
  border: '#222222',
  headerBg: '#e8eef3',
  accent: '#0d47a1',
  text: '#111111',
  muted: '#333333',
};

const tableBase: React.CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  borderCollapse: 'collapse',
  tableLayout: 'fixed',
};

const thCell: React.CSSProperties = {
  border: `1.5px solid ${theme.border}`,
  padding: '5px 8px',
  fontSize: 11,
  fontWeight: 700,
  textAlign: 'center',
  color: theme.muted,
  background: theme.headerBg,
  letterSpacing: 0.3,
};

const tdCell: React.CSSProperties = {
  border: `1.5px solid ${theme.border}`,
  padding: '4px 8px',
  fontSize: 12,
  verticalAlign: 'middle',
  wordBreak: 'break-word',
  color: theme.text,
  lineHeight: 1.35,
};

const itemTdCell: React.CSSProperties = {
  ...tdCell,
  padding: '3px 6px',
  fontSize: 12,
};

const blCell: React.CSSProperties = {
  ...itemTdCell,
  fontSize: 11,
  padding: '3px 4px',
  whiteSpace: 'nowrap',
  wordBreak: 'normal',
  letterSpacing: 0,
};

const blHeader: React.CSSProperties = {
  ...thCell,
  fontSize: 10,
  padding: '5px 4px',
  letterSpacing: 0,
};

interface PageSheetProps {
  data: ProcurementInvoicePdfData;
  page: ProcurementInvoicePdfPage;
}

function InvoiceHeader({
  dateKo,
  page,
  releaseLocation,
}: {
  dateKo: string;
  page: ProcurementInvoicePdfPage;
  releaseLocation: string;
}) {
  const release = String(releaseLocation || '').trim();
  return (
    <div style={{ marginBottom: 8, textAlign: 'center' }}>
      <div
        style={{
          display: 'inline-block',
          fontSize: 30,
          fontWeight: 700,
          letterSpacing: 10,
          color: theme.text,
          paddingBottom: 6,
          marginBottom: 10,
          borderBottom: `3px solid ${theme.accent}`,
        }}
      >
        송&nbsp;&nbsp;장
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', fontSize: 13 }}>
        <div style={{ textAlign: 'left' }}>
          <span style={{ color: theme.muted }}>
            (화주) <strong style={{ color: theme.text, fontWeight: 700 }}>{PROCUREMENT_SHIPPER_NAME}</strong>
          </span>
          {release ? (
            <div style={{ marginTop: 6, fontSize: 13, fontWeight: 600, color: theme.text }}>
              출고장소: {release}
            </div>
          ) : null}
        </div>
        <span style={{ fontWeight: 600 }}>
          {dateKo}
          {page.totalPages > 1 && (
            <span style={{ marginLeft: 10, fontSize: 12, color: theme.muted, fontWeight: 500 }}>
              ({page.pageIndex + 1}/{page.totalPages})
            </span>
          )}
        </span>
      </div>
    </div>
  );
}

function MemoBar({ memoExtra }: { memoExtra: string }) {
  return (
    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: theme.text,
        marginBottom: 8,
        padding: '8px 12px',
        background: '#fffde7',
        border: `1.5px solid #222222`,
        borderRadius: 4,
        lineHeight: 1.7,
        letterSpacing: 0.2,
      }}
    >
      {memoExtra}
    </div>
  );
}

function DestinationTable({
  data,
  landline,
  mobile,
}: {
  data: ProcurementInvoicePdfData;
  landline: string;
  mobile: string;
}) {
  return (
    <table style={{ ...tableBase, marginBottom: 8 }}>
      <colgroup>
        <col style={{ width: '38%' }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: '18%' }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thCell}>도착지</th>
          <th style={thCell}>일반전화</th>
          <th style={thCell}>휴대전화</th>
          <th style={thCell}>담당자</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ ...tdCell, height: 44 }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>{data.destinationName || '\u00A0'}</div>
            {data.destinationAddress ? (
              <div style={{ marginTop: 2, fontSize: 11, color: theme.muted }}>{data.destinationAddress}</div>
            ) : null}
          </td>
          <td style={{ ...tdCell, textAlign: 'center', fontSize: 12 }}>{landline || '\u00A0'}</td>
          <td style={{ ...tdCell, textAlign: 'center', fontSize: 12 }}>{mobile || '\u00A0'}</td>
          <td style={{ ...tdCell, textAlign: 'center', fontWeight: 600 }}>{data.destinationManager || '\u00A0'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function ItemsTable({ page }: { page: ProcurementInvoicePdfPage }) {
  return (
    <table style={{ ...tableBase, marginBottom: 8 }}>
      <colgroup>
        <col style={{ width: '19%' }} />
        <col style={{ width: '24%' }} />
        <col style={{ width: '22%' }} />
        <col style={{ width: '17%' }} />
        <col style={{ width: '18%' }} />
      </colgroup>
      <thead>
        <tr>
          <th style={blHeader}>B/L</th>
          <th style={thCell}>품목</th>
          <th style={thCell}>수량</th>
          <th style={thCell}>중량</th>
          <th style={thCell}>PLT</th>
        </tr>
      </thead>
      <tbody>
        {page.lines.map((line, idx) => {
          const hasItem = Boolean(String(line.itemName || '').trim());
          const cellBg = hasItem ? '#fff' : '#fafbfc';
          return (
            <tr key={`${page.pageIndex}-${idx}`}>
              <td style={{ ...blCell, height: PROCUREMENT_PDF_ROW_HEIGHT, textAlign: 'center', background: cellBg }}>
                {line.lineMemo || '\u00A0'}
              </td>
              <td
                style={{
                  ...itemTdCell,
                  height: PROCUREMENT_PDF_ROW_HEIGHT,
                  paddingLeft: 8,
                  fontWeight: hasItem ? 600 : 400,
                  background: cellBg,
                }}
              >
                {line.itemName || '\u00A0'}
              </td>
              <td style={{ ...itemTdCell, height: PROCUREMENT_PDF_ROW_HEIGHT, textAlign: 'center', background: cellBg }}>
                {formatShipmentQtyDisplay(line.boxQty, line.packUnit) || '\u00A0'}
              </td>
              <td style={{ ...itemTdCell, height: PROCUREMENT_PDF_ROW_HEIGHT, textAlign: 'right', background: cellBg }}>
                {formatInvoiceCellNumber(line.weightKg) || '\u00A0'}
              </td>
              <td style={{ ...itemTdCell, height: PROCUREMENT_PDF_ROW_HEIGHT, textAlign: 'right', background: cellBg }}>
                {formatInvoiceCellNumber(line.palletQty) || '\u00A0'}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function TransportTable({
  data,
  dateKo,
  arrivalKo,
}: {
  data: ProcurementInvoicePdfData;
  dateKo: string;
  arrivalKo: string;
}) {
  return (
    <table style={{ ...tableBase, marginBottom: 10 }}>
      <colgroup>
        <col style={{ width: '16%' }} />
        <col style={{ width: '34%' }} />
        <col style={{ width: '16%' }} />
        <col style={{ width: '34%' }} />
      </colgroup>
      <tbody>
        <tr>
          <td style={{ ...thCell, background: theme.headerBg }}>차량번호</td>
          <td style={{ ...tdCell, fontWeight: 600 }}>{data.vehicleNumber || '\u00A0'}</td>
          <td style={{ ...thCell, background: theme.headerBg }}>연락처</td>
          <td style={{ ...tdCell }}>{data.driverContact || '\u00A0'}</td>
        </tr>
        <tr>
          <td style={{ ...thCell, background: theme.headerBg }}>출발</td>
          <td style={{ ...tdCell }}>{dateKo}</td>
          <td style={{ ...thCell, background: theme.headerBg }}>도착</td>
          <td style={{ ...tdCell }}>{arrivalKo || '\u00A0'}</td>
        </tr>
      </tbody>
    </table>
  );
}

function InvoiceFooter({ dateKo }: { dateKo: string }) {
  return (
    <div
      style={{
        paddingTop: 12,
        borderTop: `2px solid ${theme.border}`,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        gap: 24,
      }}
    >
      <div style={{ fontSize: 13, lineHeight: 1.75, color: theme.muted }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: theme.text, marginBottom: 4 }}>
          {PROCUREMENT_SUPPLIER.name}
        </div>
        <div>TEL&nbsp;&nbsp;&nbsp;&nbsp;051 ) {PROCUREMENT_SUPPLIER.phone}</div>
        <div>FAX&nbsp;&nbsp;&nbsp;&nbsp;051 ) {PROCUREMENT_SUPPLIER.fax}</div>
        <div>담당자 : {PROCUREMENT_SUPPLIER.manager}</div>
      </div>
      <div style={{ textAlign: 'right', fontSize: 14, lineHeight: 1.8 }}>
        <div style={{ letterSpacing: 0.3 }}>상기 물품과 수량을 정히 발송함</div>
        <div style={{ marginTop: 10, fontWeight: 600, fontSize: 15 }}>{dateKo}</div>
      </div>
    </div>
  );
}

/** A4 1페이지 분량 — PDF 캡처 단위 */
export const ProcurementInvoicePageSheet: React.FC<PageSheetProps> = ({ data, page }) => {
  const dateKo = formatShipmentDateKo(data.shipmentDate);
  const arrivalKo = formatArrivalAtKo(data.arrivalAt);
  const { landline, mobile } = resolveDestinationPhones(data);
  const { memoExtra, hasMemo } = getInvoiceMemoFlags(data.memo);

  return (
    <div
      className="procurement-invoice-page-sheet"
      style={{
        width: PROCUREMENT_PDF_WIDTH,
        height: PROCUREMENT_PDF_HEIGHT,
        boxSizing: 'border-box',
        padding: '20px 32px 22px',
        fontFamily: '"Noto Sans KR", "Malgun Gothic", sans-serif',
        color: theme.text,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      <InvoiceHeader dateKo={dateKo} page={page} releaseLocation={data.releaseLocation} />
      <DestinationTable data={data} landline={landline} mobile={mobile} />
      {hasMemo && <MemoBar memoExtra={memoExtra} />}
      <ItemsTable page={page} />
      <TransportTable data={data} dateKo={dateKo} arrivalKo={arrivalKo} />
      <InvoiceFooter dateKo={dateKo} />
    </div>
  );
};

interface Props {
  data: ProcurementInvoicePdfData;
}

const ProcurementInvoicePdfTemplate: React.FC<Props> = ({ data }) => {
  const pages = buildInvoicePages(data.lines);

  return (
    <div id="procurement-invoice-pdf" style={{ width: PROCUREMENT_PDF_WIDTH, background: '#fff' }}>
      {pages.map(page => (
        <ProcurementInvoicePageSheet key={page.pageIndex} data={data} page={page} />
      ))}
    </div>
  );
};

export default ProcurementInvoicePdfTemplate;
